import { useState, useEffect } from "react";
import ConfigAPI from "../../services/ConfigAPI";

export default function ColumnMappingSection() {
    const [activeTab, setActiveTab] = useState('powerbi');
    const [powerBIColumns, setPowerBIColumns] = useState([]);
    const [icsColumns, setICSColumns] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingColumn, setEditingColumn] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    const [formData, setFormData] = useState({
        displayName: '',
        searchTerms: [''],
        required: false,
        partialMatch: true,
        fallbackValue: '-'
    });

    useEffect(() => {
        loadColumns();
    }, [activeTab]);

    const loadColumns = async () => {
        try {
            setLoading(true);
            
            if (activeTab === 'powerbi') {
                const response = await ConfigAPI.getPowerBIColumns();
                if (response.success) {
                    setPowerBIColumns(response.data);
                }
            } else {
                const response = await ConfigAPI.getICSColumns();
                if (response.success) {
                    setICSColumns(response.data);
                }
            }
        } catch (err) {
            console.error("Erro ao carregar colunas:", err);
            setError("Erro ao carregar configurações");
        } finally {
            setLoading(false);
        }
    };

    const getCurrentColumns = () => {
        return activeTab === 'powerbi' ? powerBIColumns : icsColumns;
    };

    const setCurrentColumns = (columns) => {
        if (activeTab === 'powerbi') {
            setPowerBIColumns(columns);
        } else {
            setICSColumns(columns);
        }
    };

    const handleAddSearchTerm = () => {
        setFormData(prev => ({
            ...prev,
            searchTerms: [...prev.searchTerms, '']
        }));
    };

    const handleRemoveSearchTerm = (index) => {
        setFormData(prev => ({
            ...prev,
            searchTerms: prev.searchTerms.filter((_, i) => i !== index)
        }));
    };

    const handleSearchTermChange = (index, value) => {
        setFormData(prev => ({
            ...prev,
            searchTerms: prev.searchTerms.map((term, i) => i === index ? value : term)
        }));
    };

    const handleEdit = (column) => {
        setEditingColumn(column);
        setFormData({
            displayName: column.displayName,
            searchTerms: [...column.searchTerms],
            required: column.required,
            partialMatch: column.partialMatch,
            fallbackValue: column.fallbackValue
        });
        setShowAddModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir esta configuração?')) {
            return;
        }

        try {
            setLoading(true);
            
            const deleteFunc = activeTab === 'powerbi' 
                ? ConfigAPI.deletePowerBIColumn 
                : ConfigAPI.deleteICSColumn;
            
            const response = await deleteFunc(id);
            
            if (response.success) {
                setSuccess("Coluna deletada com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                await loadColumns();
            }
        } catch (err) {
            console.error("Erro ao deletar coluna:", err);
            setError(err.response?.data?.message || "Erro ao deletar coluna");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        const validSearchTerms = formData.searchTerms.filter(term => term.trim() !== '');
        
        if (!formData.displayName.trim()) {
            alert('O nome de exibição é obrigatório');
            return;
        }

        if (validSearchTerms.length === 0) {
            alert('Adicione pelo menos um termo de busca');
            return;
        }

        try {
            setLoading(true);
            
            const data = {
                displayName: formData.displayName,
                searchTerms: validSearchTerms,
                required: formData.required,
                partialMatch: formData.partialMatch,
                fallbackValue: formData.fallbackValue
            };

            let response;
            
            if (editingColumn) {
                const updateFunc = activeTab === 'powerbi' 
                    ? ConfigAPI.updatePowerBIColumn 
                    : ConfigAPI.updateICSColumn;
                
                response = await updateFunc(editingColumn._id, data);
            } else {
                const addFunc = activeTab === 'powerbi' 
                    ? ConfigAPI.addPowerBIColumn 
                    : ConfigAPI.addICSColumn;
                
                response = await addFunc(data);
            }

            if (response.success) {
                setSuccess(editingColumn ? "Coluna atualizada com sucesso" : "Coluna criada com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                resetForm();
                setShowAddModal(false);
                await loadColumns();
            }
        } catch (err) {
            console.error("Erro ao salvar coluna:", err);
            setError(err.response?.data?.message || "Erro ao salvar coluna");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            displayName: '',
            searchTerms: [''],
            required: false,
            partialMatch: true,
            fallbackValue: '-'
        });
        setEditingColumn(null);
    };

    const handleResetToDefaults = async () => {
        if (!window.confirm('Deseja restaurar as configurações padrão? Todas as alterações serão perdidas.')) {
            return;
        }

        try {
            setLoading(true);
            
            const resetFunc = activeTab === 'powerbi' 
                ? ConfigAPI.resetPowerBIColumns 
                : ConfigAPI.resetICSColumns;
            
            const response = await resetFunc();
            
            if (response.success) {
                setSuccess("Configurações restauradas com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                await loadColumns();
            }
        } catch (err) {
            console.error("Erro ao restaurar padrões:", err);
            setError(err.response?.data?.message || "Erro ao restaurar padrões");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const currentColumns = getCurrentColumns();

    return (
        <div className="section-content">
            <h1>Mapeamento de Colunas</h1>
            <p className="card-description">
                Configure como os dados das planilhas serão interpretados pelo sistema
            </p>

            {success && (
                <div className="alert-success">
                    <span>✓ {success}</span>
                </div>
            )}

            {error && (
                <div style={{
                    background: '#fee2e2',
                    color: '#dc2626',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    fontWeight: '500'
                }}>
                    {error}
                </div>
            )}

            {/* Tabs */}
            <div className="mapping-tabs">
                <button
                    className={`tab-button ${activeTab === 'powerbi' ? 'active powerbi' : ''}`}
                    onClick={() => setActiveTab('powerbi')}
                    disabled={loading}
                >
                    🔵 Power BI
                </button>
                <button
                    className={`tab-button ${activeTab === 'ics' ? 'active ics' : ''}`}
                    onClick={() => setActiveTab('ics')}
                    disabled={loading}
                >
                    🔴 ICS
                </button>
            </div>

            {/* Lista de colunas */}
            <div className="config-card">
                <div className="card-header">
                    <h3>Colunas Configuradas ({currentColumns.length})</h3>
                    <button 
                        className="btn-secondary" 
                        onClick={handleResetToDefaults}
                        disabled={loading}
                    >
                        🔄 Restaurar Padrões
                    </button>
                </div>

                {loading && currentColumns.length === 0 ? (
                    <div className="empty-state">
                        <p>Carregando...</p>
                    </div>
                ) : currentColumns.length === 0 ? (
                    <div className="empty-state">
                        <p>Nenhuma coluna configurada ainda</p>
                    </div>
                ) : (
                    <div className="columns-list">
                        {currentColumns.map((column) => (
                            <div key={column._id} className="column-item">
                                <div className="column-main">
                                    <div className="column-header">
                                        <h4>{column.displayName}</h4>
                                        <div className="column-badges">
                                            {column.required && (
                                                <span className="badge required">Obrigatório</span>
                                            )}
                                            {column.partialMatch && (
                                                <span className="badge partial">Parcial</span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="column-details">
                                        <div className="detail-group">
                                            <span className="detail-label">Termos de busca:</span>
                                            <div className="search-terms">
                                                {column.searchTerms.map((term, idx) => (
                                                    <span key={idx} className="search-term">
                                                        "{term}"
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="detail-group">
                                            <span className="detail-label">Valor padrão:</span>
                                            <code className="fallback-value">{column.fallbackValue}</code>
                                        </div>
                                    </div>
                                </div>

                                <div className="column-actions">
                                    <button 
                                        className="btn-icon" 
                                        onClick={() => handleEdit(column)}
                                        disabled={loading}
                                    >
                                        ✏️
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                }}>
                    <div className="modal-content mapping-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{editingColumn ? 'Editar Coluna' : 'Nova Coluna'}</h2>

                        <div className="form-group">
                            <label>Nome de Exibição *</label>
                            <input
                                type="text"
                                value={formData.displayName}
                                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                                placeholder="Ex: AWB, Tipo Serviço, etc."
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label>Termos de Busca *</label>
                            <p className="helper-text">
                                Adicione os nomes das colunas que devem ser procurados na planilha
                            </p>
                            {formData.searchTerms.map((term, index) => (
                                <div key={index} className="search-term-input">
                                    <input
                                        type="text"
                                        value={term}
                                        onChange={(e) => handleSearchTermChange(index, e.target.value)}
                                        placeholder='Ex: "AWB" ou "TIPO SERVIÇO"'
                                        className="monospace"
                                        disabled={loading}
                                    />
                                    {formData.searchTerms.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn-remove"
                                            onClick={() => handleRemoveSearchTerm(index)}
                                            disabled={loading}
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button 
                                type="button" 
                                className="btn-secondary small" 
                                onClick={handleAddSearchTerm}
                                disabled={loading}
                            >
                                + Adicionar Termo
                            </button>
                        </div>

                        <div className="form-group checkbox-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={formData.required}
                                    onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                                    disabled={loading}
                                />
                                <span>Campo Obrigatório</span>
                            </label>
                            <p className="helper-text">
                                Se marcado, a linha será ignorada caso este campo não seja encontrado
                            </p>
                        </div>

                        <div className="form-group checkbox-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={formData.partialMatch}
                                    onChange={(e) => setFormData({ ...formData, partialMatch: e.target.checked })}
                                    disabled={loading}
                                />
                                <span>Correspondência Parcial</span>
                            </label>
                            <p className="helper-text">
                                Se marcado, aceita colunas que contenham o termo (Ex: "AWB" encontra "Minha AWB")
                            </p>
                        </div>

                        <div className="form-group">
                            <label>Valor Padrão (quando não encontrado)</label>
                            <input
                                type="text"
                                value={formData.fallbackValue}
                                onChange={(e) => setFormData({ ...formData, fallbackValue: e.target.value })}
                                placeholder="Ex: -, Não Informado, 0, etc."
                                disabled={loading}
                            />
                        </div>

                        <div className="modal-actions">
                            <button 
                                className="btn-secondary" 
                                onClick={() => {
                                    setShowAddModal(false);
                                    resetForm();
                                }}
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-save" 
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "Salvando..." : editingColumn ? 'Atualizar' : 'Criar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}